/**
 * Copyright 2019-2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import styles from '../../styles.module.scss';
import { Fade } from '@/components/Fade/Fade';
import { MenuNavButton } from './MenuNavButton';
import type { Props as MenuDrawerProps } from './MenuDrawer';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';

const DEFAULT_HEADER_HEIGHT = 79;

const LazyMenuDrawer = dynamic<MenuDrawerProps>(() => import('./MenuDrawer'), {
  loading: () => <ProgressModal noScroll />,
});

type Props = {
  scrolled?: boolean;
  dataTestIdBtn?: string;
  onToggle?: (open: boolean) => void;
  id?: string;
  isFixedHeader?: boolean;
  className?: string;
};

type HandleCloseOptions = {
  focusMain: boolean;
};

export function MenuNav({
  scrolled,
  dataTestIdBtn,
  onToggle,
  id,
  isFixedHeader,
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [menuTopStyle, setMenuTopStyle] = useState(DEFAULT_HEADER_HEIGHT);
  const clickBlockTimeout = useRef<NodeJS.Timeout | null>(null);
  const blockClick = useRef(false);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuNavRef = useRef<HTMLDivElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (blockClick.current) {
      return;
    }

    event.stopPropagation();
    setOpen(true);

    const headerElement = document.querySelector('#headerNavWrapper');
    const fixedHeaderElement = document.querySelector('#fixedHeaderWrapper');
    const menuTop = scrolled
      ? fixedHeaderElement?.getBoundingClientRect().bottom
      : headerElement?.getBoundingClientRect().bottom;
    if (menuTop != null) {
      setMenuTopStyle(menuTop);
    }

    if (onToggle) {
      onToggle(true);
    }
  };

  const handleClose = useCallback(
    (options: HandleCloseOptions = { focusMain: true }) => {
      setOpen(false);
      if (onToggle) {
        onToggle(false);
      }

      blockClick.current = true;
      if (clickBlockTimeout.current) {
        clearTimeout(clickBlockTimeout.current);
      }
      clickBlockTimeout.current = setTimeout(() => {
        blockClick.current = false;
      }, 300);

      if (options.focusMain) {
        document.getElementById('main-content')?.focus({ preventScroll: true });
      }
    },
    [onToggle],
  );

  useEffect(() => {
    return () => {
      if (clickBlockTimeout.current) {
        clearTimeout(clickBlockTimeout.current);
      }
    };
  }, []);

  return (
    <div
      className={cx(
        styles.navBarNav,
        { [styles.navBarNavSticky]: isFixedHeader },
        styles.navMenuContainer,
        className,
      )}
      data-testid="mobile-menu-nav-button"
      ref={menuNavRef}
    >
      <MenuNavButton
        ref={buttonRef}
        id={id}
        open={open}
        handleClick={handleClick}
        handleClose={handleClose}
        dataTestIdBtn={dataTestIdBtn}
        isFixedHeader={isFixedHeader}
      />
      <Fade open={open}>
        <LazyMenuDrawer
          scrolled={scrolled}
          open={open}
          onClose={handleClose}
          remoteButtonRef={buttonRef}
          menuTopStyle={menuTopStyle}
        />
      </Fade>
    </div>
  );
}
